import { useModals } from 'almanac-ui/Modal'
import { MultiStepWizard } from '../MultiStepWizard/MultiStepWizard'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { generatePath, useHistory } from 'react-router-dom'
import { reportError } from 'utils/error'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DesignChannel } from 'components/NewFileModal/Types'
import { DESIGN_PATH } from 'paths'
import { WizardProvider } from '../Wizard/WizardContext'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useCreateDesign } from '../DesignWizard/useCreateDesign'
import { SelectedImage } from 'types'
import { useEffect, useState } from 'react'
import { RemoveBackground } from './RemoveBackground'
import { SelectImage } from './SelectImage/SelectImage'
import { SelectTemplate } from './SelectTemplate/SelectTemplate'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showInternalServerErrorMessage } from 'common/ToastMessage/utils'
import { MarketplaceSelectImageWithNameSchema } from '../schemas/BaseDesign'
import { DesignMarketplaceWizardProps, Values } from './Types'

export const DesignMarketplaceWizard = (props: DesignMarketplaceWizardProps) => {
  const { closeAllModals } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const defaultFolder = useDefaultFolder()
  const { currentFolder } = useCurrentFolderStore()
  const { mutateAsync, isLoading } = useCreateDesign({ workspaceId })
  const history = useHistory()
  const [selectedImage, setSelectedImage] = useState<SelectedImage>()
  const [canRemoveBackground, setCanRemoveBackground] = useState(false)
  const [hasRemovedBackground, setHasRemovedBackground] = useState(false)
  const [cutoutUrl, setCutoutUrl] = useState<string>()

  const startWithTemplate = props.initialValues.startWithTemplate
  const schema = startWithTemplate
    ? { ...props.schema, steps: [MarketplaceSelectImageWithNameSchema, props.schema.steps[1]] }
    : props.schema

  useEffect(() => {
    if (selectedImage?.variants?.some((v) => !!v.transformations.remove_background) || !hasRemovedBackground) return

    const fetchMediaFile = async () => {
      if (!selectedImage?.id) return

      const response = await MediaFileClient.getAlmanacFile(selectedImage.id)
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        setSelectedImage(response.data.almanac_file as SelectedImage)
      }
    }

    fetchMediaFile()
  }, [hasRemovedBackground, selectedImage])

  const createDesignMutation = async ({
    width,
    height,
    initial_media_file_id,
    initial_media_file_remove_background,
    source_visual_element_id,
    brand_visual_kit_id,
    filename,
    project,
    brand_kit_id,
  }: Values) => {
    try {
      const projectId = project?.id || currentFolder?.id || defaultFolder?.id || ''

      const generatingToast = showToastMessage('Creating post...', 'generating', undefined, 0)
      const design = await mutateAsync({
        width,
        height,
        title: filename,
        parent_id: projectId,
        channel: props.schema.meta.channel as DesignChannel,
        source_visual_element_id,
        initial_media_file_id,
        initial_media_file_remove_background,
        brand_visual_kit_id,
        cross_post_accounts: [],
        seed: { apply_brand: false },
        brand_kit_id,
        completed_actions: {
          template: true,
          generate_caption: false,
          core_image: true,
          apply_brand: false,
        },
      }).finally(() => {
        generatingToast()
      })

      if (!design) {
        throw new Error('No almanac file found')
      }

      props.onSuccess?.({ almanac_file: design })

      closeAllModals()
      history.push(generatePath(DESIGN_PATH, { key: design.id }))
    } catch (error) {
      reportError(error)
      showInternalServerErrorMessage()
    }
  }

  const handleSubmit = async (values: Values) => {
    return createDesignMutation(values)
  }

  return (
    <WizardProvider schema={schema}>
      <MultiStepWizard.Root
        {...props}
        schema={schema}
        onSubmit={handleSubmit}
        initialValues={{ project: currentFolder || defaultFolder, ...props.initialValues }}
      >
        <SelectImage
          isLoading={isLoading}
          onImageChange={setSelectedImage}
          setCanRemoveBackground={setCanRemoveBackground}
          createDesign={createDesignMutation}
        />
        {selectedImage ? (
          <RemoveBackground
            selectedImage={selectedImage}
            canRemoveBackground={canRemoveBackground}
            onChangeCanRemoveBackground={setCanRemoveBackground}
            hasRemovedBackground={hasRemovedBackground}
            onChangeRemovedBackground={setHasRemovedBackground}
            cutoutUrl={cutoutUrl}
            setCutoutUrl={setCutoutUrl}
          />
        ) : (
          <div />
        )}
        {selectedImage?.id && hasRemovedBackground && !startWithTemplate && (
          <SelectTemplate selectedImage={selectedImage} />
        )}
      </MultiStepWizard.Root>
    </WizardProvider>
  )
}
